import React, { memo } from 'react';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import TextFieldWithDebounce from 'ui/WithDebounce/TextFieldWithDebounce';
import {
    PROJECTION_DETAIL_AMOUNT,
    PROJECTION_DETAIL_COMMENT,
} from 'routes/BudgetModule/config/constants';
import { startAdornment } from 'ui/AgGridReact/CurrencyInputAdornment';
import { useScrollessRef } from 'hooks/helpers';
import AmountFormat from 'components/NumberFormat/AmountFormat';
import {
    getNewFormattedValue,
    getVentilationFromNode,
} from '../helpers/dataHelpers';
import {
    AtterrissageProjectionUpdateFieldT,
    ProjectionsDimensionsMapping,
    AtterrissageProjectionDetails,
} from '../helpers/types';
import { useUpdateAtterrissageProjectionDetails } from '../hooks/atterrissage';
import { UpdateProjectionDetailsParams } from '../helpers/colDefinitions';
import { StyProjectionDetailsCellRenderer } from '../helpers/styles';
import { ProjectionTooltip } from './ProjectionToolTip';
import { TextFieldProps } from '@mui/material';

export type AtterrissageProjectionDetailsCellRendererCustomParams = {
    resourceSoftwareId: string | undefined;
    projectionDetailsUpdateTracker: (
        field: AtterrissageProjectionUpdateFieldT['field'],
    ) => void;
    dimensionsMapping: ProjectionsDimensionsMapping;
    projectionIdLabelMapping: Record<string, string>;
};

type AtterrissageProjectionDetailsCellRendererParams =
    CellRendererParams<AtterrissageProjectionDetails.WithMetadata> & {
        value?: number;
        textFieldProps: TextFieldProps;
        customParams: AtterrissageProjectionDetailsCellRendererCustomParams;
        field: 'projectionDetails.amount' | 'projectionDetails.comment';
        updateProjectionParams: UpdateProjectionDetailsParams;
        defaultValue?: number;
    };

function AtterrissageProjectionDetailCellRenderer({
    value,
    node,
    field,
    defaultValue,
    textFieldProps,
    customParams,
    updateProjectionParams,
}: AtterrissageProjectionDetailsCellRendererParams): React.ReactElement {
    // We need this ref to disable input changing with mouse wheel
    const ref = useScrollessRef();

    const { budgetProposal, expenseOrIncome, isDaf } = updateProjectionParams;

    const { resourceSoftwareId, projectionDetailsUpdateTracker } = customParams;
    const dimensions = getVentilationFromNode(node);

    const updateProjectionMutation = useUpdateAtterrissageProjectionDetails({
        budgetProposal,
        expenseOrIncome,
        isDaf,
        dimensions,
    });

    if (value === undefined) {
        return <span />;
    }

    const hasNoBudgetLine = value === null;
    if (!node.leafGroup) {
        if (field === PROJECTION_DETAIL_COMMENT) {
            return <span />;
        }

        return (
            <AmountFormat
                value={value}
                defaultValue={value}
                fixedDecimalScale
            />
        );
    }

    const onValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        const fieldToUpdate = field.split(
            '.',
        )[1] as AtterrissageProjectionUpdateFieldT['field'];
        const finalValue = getNewFormattedValue(
            newValue,
            defaultValue,
            fieldToUpdate,
        );
        projectionDetailsUpdateTracker(fieldToUpdate);
        updateProjectionMutation.mutate({
            field: fieldToUpdate,
            value: finalValue,
            resourceSoftwareId,
        });
    };

    const canEdit = node?.allLeafChildren?.[0]?.data?.canEdit ?? false;
    return (
        <ProjectionTooltip
            disabled={hasNoBudgetLine}
            disabledByResource={!canEdit}
            by={budgetProposal.by}
        >
            <StyProjectionDetailsCellRenderer>
                <TextFieldWithDebounce
                    className="mui-text-field-input"
                    value={value || defaultValue}
                    disabled={!canEdit || hasNoBudgetLine}
                    fullWidth
                    variant="outlined"
                    size="small"
                    onChange={onValueChange}
                    InputProps={{
                        disableInjectingGlobalStyles: true,
                        startAdornment:
                            field === PROJECTION_DETAIL_AMOUNT
                                ? startAdornment
                                : undefined,
                    }}
                    {...textFieldProps}
                    ref={ref}
                />
            </StyProjectionDetailsCellRenderer>
        </ProjectionTooltip>
    );
}

export default memo(AtterrissageProjectionDetailCellRenderer);
